import { createUrl, createURLWithTokenAndCustomPath, restGet, restPost } from '~/src/dash/api/rest';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { Order } from '~/src/dash/components/tables/ListViewTable/common';

export enum InventoryRuleType {
  UNKNOWN = 0,
  VMAP = 1,
  VAST = 2,
  ORTB = 3,
}

export enum InventoryRuleResponseType {
  UNKNOWN = 0,
  INLINE_VAST = 1,
  AD_TAG_URI = 2,
}

export enum InventoryRuleTimezoneType {
  UNKNOWN = 0,
  VIEWER = 1,
  PUBLISHER = 2,
}

export interface IInventoryRuleAdBreak {
  AdBreakID: number;
  PodDuration: number;
  SlotCount: number;
  MinAdDuration: number;
  MaxAdDuration: number;
  CuePointType: number;
  CuePointValue: string;
}

export interface IInventoryRuleContentData {
  ID: number;
  Name: string;
  ContentID: string;
  ContentCategory: string;
  ContentChannel: string;
  ContentContext: string;
  ContentDistributionID: string;
  ContentDistributionName: string;
  ContentEpisode: string;
  ContentGenre: string;
  ContentKeywords: string;
  ContentLanguage: string;
  ContentLength: number;
  ContentNetwork: string;
  ContentProdQuality: string;
  ContentProducerID: string;
  ContentProducer: string;
  ContentRating: string;
  ContentSeason: string;
  ContentSeries: string;
  ContentTaxonomy: string;
  ContentTitle: string;
  ContentURL: string;
}

export interface IInventoryRule {
  ID: number;
  Name: string;
  TimezoneType: InventoryRuleTimezoneType;
  LookupKey: string;
  LookupValue: string;
  RuleType: InventoryRuleType;
  ResponseType: InventoryRuleResponseType;
  StartDateTime: string;
  EndDateTime: string;
  ContentID: string;
  ContentCategory: string;
  ContentChannel: string;
  ContentContext: string;
  ContentDistributionID: string;
  ContentDistributionName: string;
  ContentEpisode: string;
  ContentGenre: string;
  ContentKeywords: string;
  ContentLanguage: string;
  ContentLength: number;
  ContentNetwork: string;
  ContentProdQuality: string;
  ContentProducerID: string;
  ContentProducer: string;
  ContentRating: string;
  ContentSeason: string;
  ContentSeries: string;
  ContentTaxonomy: string;
  ContentTitle: string;
  ContentURL: string;
  Custom5: string;
  Custom6: string;
  Custom7: string;
  Custom8: string;
  Custom9: string;
  Custom10: string;
  Custom11: string;
  Custom12: string;
  Custom13: string;
  Custom14: string;
  Custom15: string;
  Custom16: string;
  Custom17: string;
  Custom18: string;
  AdBreaks: IInventoryRuleAdBreak[];
  Content: IInventoryRuleContentData[];
  Customs: unknown[];
}

export interface IGetInventoryRulesData {
  InventoryRules: IInventoryRule[];
  TotalCount: number;
}

export interface IInventoryRulesQueryParams {
  Search?: string;
  LookupKey?: string;
  StartDateFrom?: string;
  StartDateTo?: string;
  EndDateFrom?: string;
  EndDateTo?: string;
  RuleType?: number;
  ContentGenre?: string[];
  ContentTitle?: string[];
  ContentNetwork?: string[];
  ContentCategory?: string[];
  ContentKeywords?: string[];
  Page: number;
  PageSize: number;
  OrderBy: string;
  OrderDirection: Order;
}

export async function getInventoryRulesData(
  publisherID: string,
  query: IInventoryRulesQueryParams,
  signal?: AbortSignal,
): Promise<IGetInventoryRulesData> {
  const reqUrl = `${createUrl('/inventory_rules_v2/search', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query, undefined, undefined, signal);
  return (
    (response?.parsedBody as IGetInventoryRulesData) || {
      InventoryRules: [],
      TotalCount: 0,
    }
  );
}

export async function getInventoryRulesContentFilters(
  publisherID: string,
  fields: string[],
): Promise<Record<string, ISelectOption[]>> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    '/uimd/inv_rule_content_data',
  )}&publisher_id=${publisherID}&flatten=true&fields=${fields.join(',')}`;
  const response = await restGet<unknown>(reqUrl, {});
  return (
    (response?.parsedBody as Record<string, ISelectOption[]>) || {
      ContentCategory: [],
      ContentGenre: [],
      ContentNetwork: [],
      ContentTitle: [],
    }
  );
}

const contentFields = ['ContentCategory', 'ContentGenre', 'ContentNetwork', 'ContentTitle'];

const inventoryRuleFields = ['LookupKey'];

export async function getInventoryRulesFilters(
  publisherID: string,
): Promise<Record<string, ISelectOption[]>> {
  const reqUrl = `${createURLWithTokenAndCustomPath(
    '/uimd',
  )}&publisher_id=${publisherID}&flatten=true&model=inv_rule_content_data:${contentFields.join(
    ',',
  )}%7Cinventory_rule:${inventoryRuleFields.join(',')}`;
  const response = await restGet<unknown>(reqUrl, {});
  return (
    (response?.parsedBody as Record<string, ISelectOption[]>) || {
      LookupKey: [],
    }
  );
}

export interface IInventoryRulesPublisherSettings {
  TimezoneType: InventoryRuleTimezoneType;
}

export async function getInventoryRulesPublisherSettings(
  publisherID: string,
): Promise<InventoryRuleTimezoneType> {
  const reqUrl = `${createUrl('/inv_rule_publisher_setting', publisherID, 'v1')}`;
  const response = await restGet<unknown>(reqUrl, {});
  return (
    (response?.parsedBody as IInventoryRulesPublisherSettings).TimezoneType ||
    InventoryRuleTimezoneType.UNKNOWN
  );
}

export async function updateInventoryRulesPublisherSettings(
  publisherID: string,
  timezoneType: InventoryRuleTimezoneType,
  signal?: AbortSignal,
): Promise<InventoryRuleTimezoneType> {
  const reqUrl = `${createUrl('/inv_rule_publisher_setting', publisherID, 'v1')}`;
  const response = await restPost<unknown>(
    reqUrl,
    { TimezoneType: timezoneType },
    undefined,
    undefined,
    signal,
  );
  return (
    (response?.parsedBody as IInventoryRulesPublisherSettings).TimezoneType ||
    InventoryRuleTimezoneType.UNKNOWN
  );
}
